import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  CheckCircle as VerifiedIcon,
  EmojiEvents as ContestIcon,
  Handshake as HandshakeIcon,
  Language as GlobalIcon,
  Pending as PendingIcon,
} from '@mui/icons-material';
import SponsorPublicShell from '@components/sponsor/SponsorPublicShell';
import {
  getBudgetLabel,
  getOpportunityTypeLabel,
  getTierLabel,
  tierGradient,
  type SponsorshipOpportunity,
} from '@utils/sponsor';
import { useAuthStore } from '@store/authStore';

interface SponsorProfile {
  companyName: string;
  contactName?: string;
  contactEmail?: string;
  profileStatus?: string;
  preferredTier?: string;
  budgetBand?: string;
  country?: string;
  sports?: string[];
  interestCategories?: string[];
}

interface SponsorApplication {
  id: string;
  status: string;
  applicationType?: string;
  createdAt?: string;
  opportunityId?: string;
}

interface SponsorPlacement {
  id: string;
  sport: string;
  contestTaskId: string;
  sponsorshipTier?: string;
  displayLabel?: string;
}

interface DashboardData {
  profile: SponsorProfile;
  applications: SponsorApplication[];
  placements: SponsorPlacement[];
  openOpportunities: SponsorshipOpportunity[];
  autoApproveEnabled?: boolean;
}

const statusChip = (status?: string) => {
  const map: Record<string, 'success' | 'warning' | 'info' | 'error' | 'default'> = {
    verified: 'success',
    approved: 'success',
    submitted: 'warning',
    under_review: 'info',
    pending: 'warning',
    declined: 'error',
  };
  return map[String(status || '')] || 'default';
};

const SponsorDashboardPage: React.FC = () => {
  const { user } = useAuthStore();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const res = await fetch('/api/sponsor/me', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || 'Failed to load dashboard');
      setData(json.data);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const profile = data?.profile;
  const hero = (
    <Box sx={{ py: { xs: 4, md: 5 } }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <HandshakeIcon sx={{ color: '#f59e0b' }} />
        <Typography variant="overline" sx={{ color: '#94a3b8', letterSpacing: 2 }}>
          Sponsor partner portal
        </Typography>
      </Stack>
      <Typography variant="h4" sx={{ fontWeight: 900, color: '#f8fafc', mb: 0.5 }}>
        {profile?.companyName || user?.companyName || 'Partnership dashboard'}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip
          icon={profile?.profileStatus === 'verified' ? <VerifiedIcon /> : <PendingIcon />}
          label={profile?.profileStatus === 'verified' ? 'Verified partner' : 'Profile pending'}
          color={statusChip(profile?.profileStatus)}
          size="small"
          sx={{ fontWeight: 700 }}
        />
        {profile?.preferredTier && (
          <Chip label={`Tier: ${getTierLabel(profile.preferredTier)}`} size="small" sx={{ bgcolor: 'rgba(99,102,241,0.2)', color: '#c7d2fe' }} />
        )}
        {profile?.country && (
          <Chip icon={<GlobalIcon />} label={profile.country} size="small" sx={{ color: '#e2e8f0' }} />
        )}
      </Stack>
    </Box>
  );

  return (
    <SponsorPublicShell hero={hero}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress sx={{ color: '#f59e0b' }} />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : data ? (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={cardSx}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#f1f5f9', mb: 2 }}>
                  Your profile
                </Typography>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      Contact
                    </Typography>
                    <Typography sx={{ color: '#e2e8f0' }}>{profile?.contactName}</Typography>
                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                      {profile?.contactEmail}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      Budget band
                    </Typography>
                    <Typography sx={{ color: '#e2e8f0' }}>{getBudgetLabel(profile?.budgetBand)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      Sports
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      {(profile?.sports || []).map((s) => (
                        <Chip key={s} label={s} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: '#e2e8f0' }} />
                      ))}
                    </Stack>
                  </Box>
                </Stack>
                <Button
                  component={RouterLink}
                  to="/sponsors/opportunities"
                  fullWidth
                  variant="contained"
                  startIcon={<CampaignIcon />}
                  sx={{
                    mt: 3,
                    textTransform: 'none',
                    fontWeight: 800,
                    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                  }}
                >
                  Browse opportunities
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card sx={{ ...cardSx, mb: 3 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#f1f5f9' }}>
                    Active contest placements
                  </Typography>
                  <Chip label={`${data.placements.length} linked`} size="small" sx={{ color: '#94a3b8' }} />
                </Stack>
                {data.placements.length === 0 ? (
                  <Alert severity="info" sx={{ bgcolor: 'rgba(59,130,246,0.1)', color: '#93c5fd' }}>
                    No contest sponsorships linked yet. Admin assigns placements via{' '}
                    <code style={{ color: '#fcd34d' }}>contest_sponsors</code> after partnership confirmation.
                  </Alert>
                ) : (
                  <Stack spacing={1.5}>
                    {data.placements.map((p) => (
                      <Box
                        key={p.id}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          background: tierGradient(p.sponsorshipTier),
                          border: '1px solid rgba(255,255,255,0.12)',
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <ContestIcon sx={{ color: '#fff' }} />
                          <Typography sx={{ fontWeight: 800, color: '#fff' }}>
                            {p.displayLabel || 'Presented by'} · {p.sport}
                          </Typography>
                          <Chip label={getTierLabel(p.sponsorshipTier)} size="small" sx={{ ml: 'auto', bgcolor: 'rgba(0,0,0,0.25)', color: '#fff' }} />
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>

            <Card sx={cardSx}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#f1f5f9', mb: 2 }}>
                  Your applications
                </Typography>
                {data.applications.length === 0 ? (
                  <Typography sx={{ color: '#94a3b8' }}>No applications yet.</Typography>
                ) : (
                  <Stack spacing={1}>
                    {data.applications.map((app) => (
                      <Box
                        key={app.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(255,255,255,0.06)',
                        }}
                      >
                        <Typography sx={{ color: '#e2e8f0', flex: 1 }}>
                          {app.applicationType === 'account_registration' ? 'Account registration' : 'Partnership interest'}
                        </Typography>
                        <Chip label={app.status} size="small" color={statusChip(app.status)} />
                        {app.createdAt && (
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            {new Date(app.createdAt).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}
                {data.autoApproveEnabled && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#64748b' }}>
                    Phase 1: applications are auto-approved. Manual review will apply as the platform scales.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {data.openOpportunities.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#f1f5f9', mb: 2 }}>
                Open opportunities
              </Typography>
              <Grid container spacing={2}>
                {data.openOpportunities.slice(0, 4).map((opp) => (
                  <Grid item xs={12} sm={6} key={opp.id}>
                    <Card sx={{ ...cardSx, height: '100%' }}>
                      <CardContent>
                        <Chip
                          label={getOpportunityTypeLabel(opp.opportunityType)}
                          size="small"
                          sx={{ mb: 1, bgcolor: 'rgba(245,158,11,0.15)', color: '#fcd34d' }}
                        />
                        <Typography sx={{ fontWeight: 800, color: '#f1f5f9', mb: 1 }}>{opp.title}</Typography>
                        <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }} noWrap>
                          {opp.description}
                        </Typography>
                        <Button component={RouterLink} to="/sponsors/opportunities" size="small" sx={{ color: '#a5b4fc', fontWeight: 700 }}>
                          View & apply
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
        </Grid>
      ) : null}
    </SponsorPublicShell>
  );
};

const cardSx = {
  bgcolor: 'rgba(30,41,59,0.85)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 3,
  boxShadow: '0 16px 48px rgba(0,0,0,0.35)',
};

export default SponsorDashboardPage;
